import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isOpenIsh } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import type { PickabilityStatus } from "@redotech/redo-fulfillment-group-common/pickability-status";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { StatusDot } from "@redotech/redo-web/voice/shared/status-dot";
import { getExpandedItems } from "../../utils/outbound-labels-utils";
import { LotBadge } from "../../wms/shared/lot-badge";

type LineItemPickability = NonNullable<
  FulfillmentGroupData["lineItemPickability"]
>[number];

function getWoLabel(item: LineItemPickability): string | null {
  // Prefer the per-ordinal counts on v24+ docs — "3/5 ready" tells the
  // merchant exactly how much of the line item is produced. Fall back to
  // the legacy single-status summary on older docs.
  if (
    item.quantityReady != null &&
    item.quantityPending != null &&
    item.quantityPending > 0
  ) {
    return `${item.quantityReady}/${item.quantity} produced (${item.quantityPending} in production)`;
  }
  if (!item.workOrderStatus) {
    return null;
  }
  if (item.workOrderStatus === "not_started") {
    return "WO queued";
  }
  if (item.workOrderStep) {
    return `WO: ${item.workOrderStep}`;
  }
  return "WO in progress";
}

// One colored status pip per ordinal — completed (green), in_progress (blue),
// not_started (gray), on_hold (yellow), cancelled / wasted (faded). Renders a
// compact strip the merchant can read at a glance: ✓✓⏵⏳⏳ for "2 done, 1 in
// progress, 2 not started". Falls back to null when work_orders isn't
// populated (pre-v24 docs or non-WO line items).
function getOrdinalDotColor(
  status: string,
): "green" | "yellow" | "gray" | "red" | null {
  switch (status) {
    case "completed":
      return "green";
    case "in_progress":
      return "yellow";
    case "on_hold":
      return "red";
    case "not_started":
      return "gray";
    case "cancelled":
    case "wasted":
    case "cancellation_pending":
      return null; // hide terminal-non-success ordinals from the strip
    default:
      return "gray";
  }
}

function renderWorkOrderOrdinalStrip(
  item: LineItemPickability,
): JSX.Element | null {
  if (!item.workOrders || item.workOrders.length === 0) {
    return null;
  }
  const visible = item.workOrders.filter(
    (wo) => getOrdinalDotColor(wo.status) !== null,
  );
  if (visible.length === 0) {
    return null;
  }
  return (
    <Tooltip
      title={
        <Flex flexDirection="column" gap="xxs">
          {item.workOrders.map((wo) => (
            <Text fontSize="xxs" key={`ordinal-detail-${wo.ordinal}`}>
              {`Unit ${wo.ordinal}: ${wo.status}${wo.step ? ` (${wo.step})` : ""}`}
            </Text>
          ))}
        </Flex>
      }
    >
      <Flex align="center" gap="xxs">
        {visible.map((wo) => {
          const color = getOrdinalDotColor(wo.status)!;
          return <StatusDot color={color} key={`ordinal-dot-${wo.ordinal}`} />;
        })}
      </Flex>
    </Tooltip>
  );
}

function getDisplayTitle(item: LineItemPickability): string {
  const variant = item.variantTitle;
  if (variant && variant !== "Default Title") {
    return `${item.productTitle} - ${variant}`;
  }
  return item.productTitle || item.title;
}

function hasFreshInventory(item: LineItemPickability): boolean {
  if (!item.pickableUntil) {
    return true;
  }
  return item.pickableUntil.getTime() > Date.now();
}

// Whether the item is pickable on its own inventory merits (independent of the
// allowed-problem-pick policy), including the incremental/uncounted-bin case.
function isInventoryPickable(item: LineItemPickability): boolean {
  return (
    (item.isPickableIncremental ?? item.isPickable) && hasFreshInventory(item)
  );
}

// True when the item has already been fully picked — nothing left to pick.
function isAlreadyPicked(item: LineItemPickability): boolean {
  return item.isUnpicked === false;
}

// Availability/pickability description shown for a line item in the tooltip.
function getLineItemAvailabilityText(item: LineItemPickability): string {
  if (item.hasNoProduct) {
    return "Unknown item";
  }
  if (item.isNonPicked) {
    return "Non-picking";
  }
  if (isAlreadyPicked(item)) {
    return "Already picked";
  }
  // Only surface the policy reason when inventory/demand wouldn't make it
  // pickable on its own — otherwise show the normal availability detail.
  if (item.allowedByPolicy && !isInventoryPickable(item)) {
    return "Pickable by policy";
  }
  if (!hasFreshInventory(item)) {
    return `${item.quantityAvailable} expired`;
  }
  if (item.isPickable) {
    return item.quantityCommitted
      ? `${item.quantityAvailable} avail (${item.quantityCommitted} committed)`
      : `${item.quantityAvailable} avail`;
  }
  if (item.hasUncountedBin && item.quantityAvailable === 0) {
    return `-/${item.quantity} avail (uncounted)`;
  }
  if (item.quantityCommitted && item.quantityCommitted > 0) {
    const free = Math.max(
      0,
      item.quantityAvailable -
        item.quantityCommitted -
        (item.hasImsInventoryItemId ? 0 : (item.demand ?? 0)),
    );
    const reserved =
      !item.hasImsInventoryItemId && item.demand
        ? `, ${item.demand} reserved`
        : "";
    return `${free}/${item.quantity} free (${item.quantityCommitted} committed${reserved})`;
  }
  if (!item.hasImsInventoryItemId && item.demand && item.demand > 0) {
    return `${Math.max(0, item.quantityAvailable - item.demand)}/${item.quantity} free (${item.demand} reserved)`;
  }
  return `${item.quantityAvailable}/${item.quantity} avail`;
}

// Badge text shown for a single expanded line item in the pickability column.
function getExpandedPickabilityBadgeText(item: LineItemPickability): string {
  if (isAlreadyPicked(item)) {
    return "Already picked";
  }
  if (item.allowedByPolicy && !isInventoryPickable(item)) {
    return "Pickable by policy";
  }
  if (!hasFreshInventory(item)) {
    return `${item.quantityAvailable} expired`;
  }
  if (item.isPickable) {
    return item.quantityCommitted
      ? `${item.quantityAvailable} avail (${item.quantityCommitted} committed)`
      : `${item.quantityAvailable} avail`;
  }
  if (item.hasUncountedBin) {
    return `-/${item.quantity} avail (uncounted)`;
  }
  if (item.quantityCommitted && item.quantityCommitted > 0) {
    const free = Math.max(
      0,
      item.quantityAvailable -
        item.quantityCommitted -
        (item.hasImsInventoryItemId ? 0 : (item.demand ?? 0)),
    );
    const reserved =
      !item.hasImsInventoryItemId && item.demand
        ? `, ${item.demand} reserved`
        : "";
    return `${free}/${item.quantity} free (${item.quantityCommitted} committed${reserved})`;
  }
  if (!item.hasImsInventoryItemId && item.demand && item.demand > 0) {
    return `${Math.max(0, item.quantityAvailable - item.demand)}/${item.quantity} free (${item.demand} reserved)`;
  }
  return `${item.quantityAvailable}/${item.quantity} avail`;
}

type SortedWorkOrder = NonNullable<LineItemPickability["workOrders"]>[number];

function workOrderProgressRank(status: string): number | null {
  switch (status) {
    case "completed":
      return 0;
    case "in_progress":
      return 1;
    case "on_hold":
      return 2;
    case "not_started":
      return 3;
    default:
      return null;
  }
}

function sortWorkOrdersByProgress(
  workOrders: NonNullable<LineItemPickability["workOrders"]>,
): SortedWorkOrder[] {
  return workOrders
    .filter((wo) => workOrderProgressRank(wo.status) !== null)
    .slice()
    .sort((a, b) => {
      const rankA = workOrderProgressRank(a.status)!;
      const rankB = workOrderProgressRank(b.status)!;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return a.ordinal - b.ordinal;
    });
}

type PickabilityBin = NonNullable<LineItemPickability["bins"]>[number];

/** Cap the tooltip so a merchant with 200 bins per product doesn't blow up the popover. */
const MAX_TOOLTIP_BINS = 10;

function formatBinExpiration(date: Date): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Suffix for the expiration-only case. When a lot number exists we render
 * <LotBadge/> alongside the text (badge handles the expiration itself).
 */
function getBinExpirationOnlySuffix(bin: PickabilityBin): string {
  if (bin.lotNumber) {
    return "";
  }
  if (bin.expirationDate) {
    return ` · exp ${formatBinExpiration(bin.expirationDate)}`;
  }
  return "";
}

function renderBinText(bin: PickabilityBin): JSX.Element {
  const isNonPickBin =
    bin.storageLocationType === "receiving" ||
    bin.storageLocationType === "storage";
  const isCustom = bin.inventoryItemClass === "custom";
  const suffix = getBinExpirationOnlySuffix(bin);
  return (
    <Flex align="center" gap="xxs" key={bin.storageLocationId}>
      <Text
        fontSize="xxs"
        textColor={
          isNonPickBin
            ? "tertiary"
            : bin.quantity > 0
              ? "success"
              : bin.hasPendingReplenishment
                ? "warning"
                : bin.lastCountedAt === null
                  ? "success"
                  : "secondary"
        }
      >
        {bin.storageLocationName}:{" "}
        {bin.quantity > 0
          ? bin.hasPendingReplenishment
            ? `${bin.quantity} (replenishing)`
            : bin.quantity
          : bin.lastCountedAt === null
            ? bin.hasPendingReplenishment
              ? "replenishing"
              : "uncounted"
            : bin.hasPendingReplenishment
              ? "0 (replenishing)"
              : 0}
        {suffix}
      </Text>
      {bin.lotNumber && (
        <LotBadge
          expirationDate={bin.expirationDate ?? null}
          lotNumber={bin.lotNumber}
          size="xs"
        />
      )}
      {isCustom && (
        <RedoBadge color="blue" size="xs" text="Custom" type="alternate" />
      )}
    </Flex>
  );
}

function renderBinsBlock(bins: PickabilityBin[]): JSX.Element | null {
  if (bins.length === 0) {
    return null;
  }
  return (
    <Flex flexDirection="column" gap="none">
      {bins.slice(0, 1).map(renderBinText)}
      {bins.length > 1 && (
        <Tooltip
          leftAlign
          title={
            <Flex flexDirection="column" gap="xxs">
              {bins.slice(0, MAX_TOOLTIP_BINS).map((bin) => {
                const isNonPickBin =
                  bin.storageLocationType === "receiving" ||
                  bin.storageLocationType === "storage";
                return (
                  <Flex align="center" gap="xxs" key={bin.storageLocationId}>
                    <Text
                      fontSize="xxs"
                      textColor={isNonPickBin ? "tertiary" : undefined}
                    >
                      {bin.storageLocationName}:{" "}
                      {bin.quantity > 0
                        ? bin.hasPendingReplenishment
                          ? `${bin.quantity} (replenishing)`
                          : bin.quantity
                        : bin.lastCountedAt === null
                          ? bin.hasPendingReplenishment
                            ? "replenishing"
                            : "uncounted"
                          : bin.hasPendingReplenishment
                            ? "0 (replenishing)"
                            : 0}
                      {getBinExpirationOnlySuffix(bin)}
                    </Text>
                    {bin.lotNumber && (
                      <LotBadge
                        expirationDate={bin.expirationDate ?? null}
                        lotNumber={bin.lotNumber}
                        size="xs"
                        type="alternate"
                      />
                    )}
                    {bin.inventoryItemClass === "custom" && (
                      <RedoBadge
                        color="blue"
                        size="xs"
                        text="Custom"
                        type="alternate"
                      />
                    )}
                  </Flex>
                );
              })}
              {bins.length > MAX_TOOLTIP_BINS && (
                <Text fontSize="xxs" textColor="secondary">
                  … and {bins.length - MAX_TOOLTIP_BINS} more
                </Text>
              )}
            </Flex>
          }
        >
          <div
            style={{
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: "3px",
            }}
          >
            <Text fontSize="xxs" textColor="secondary">
              +{bins.length - 1} more
            </Text>
          </div>
        </Tooltip>
      )}
    </Flex>
  );
}

function renderOrdinalExpandedRow(
  wo: SortedWorkOrder | undefined,
  bins: PickabilityBin[],
  itemIsAlreadyPicked: boolean,
): JSX.Element {
  if (!wo) {
    return (
      <Flex align="flex-start" flexDirection="column" gap="xs" pl="lg">
        <RedoBadge
          color="gray"
          segmentLeading={{ type: "icon", Icon: DotIcon }}
          size="xs"
          text="Awaiting WO"
          type="primary"
        />
      </Flex>
    );
  }

  const isCompleted = wo.status === "completed";

  const badge = (() => {
    if (isCompleted) {
      return itemIsAlreadyPicked
        ? { color: "gray" as const, text: "Already picked" }
        : { color: "success" as const, text: "Ready" };
    }
    if (wo.status === "in_progress") {
      return {
        color: "warning" as const,
        text: wo.step ? `WO: ${wo.step}` : "WO in progress",
      };
    }
    if (wo.status === "on_hold") {
      return {
        color: "error" as const,
        text: wo.step ? `On hold: ${wo.step}` : "On hold",
      };
    }
    return { color: "gray" as const, text: "WO queued" };
  })();

  return (
    <Flex align="flex-start" flexDirection="column" gap="xs" pl="lg">
      <Flex align="center" gap="sm">
        <RedoBadge
          color={badge.color}
          segmentLeading={{ type: "icon", Icon: DotIcon }}
          size="xs"
          text={badge.text}
          type="primary"
        />
      </Flex>
      {isCompleted && renderBinsBlock(bins)}
    </Flex>
  );
}

function renderLineItemDetails(item: LineItemPickability) {
  const ordinalStrip = renderWorkOrderOrdinalStrip(item);
  const sortedBins = [...(item.bins ?? [])].sort((a, b) => {
    const aNonBin =
      a.storageLocationType === "receiving" ||
      a.storageLocationType === "storage"
        ? 1
        : 0;
    const bNonBin =
      b.storageLocationType === "receiving" ||
      b.storageLocationType === "storage"
        ? 1
        : 0;
    if (aNonBin !== bNonBin) {
      return aNonBin - bNonBin;
    }
    const aSecondary = a.priority === "secondary" ? 1 : 0;
    const bSecondary = b.priority === "secondary" ? 1 : 0;
    if (aSecondary !== bSecondary) {
      return aSecondary - bSecondary;
    }
    const aExp = a.expirationDate?.getTime() ?? Number.POSITIVE_INFINITY;
    const bExp = b.expirationDate?.getTime() ?? Number.POSITIVE_INFINITY;
    if (aExp !== bExp) {
      return aExp - bExp;
    }
    return b.quantity - a.quantity;
  });
  return (
    <Flex flexDirection="column" gap="none" key={item.id}>
      <Text fontSize="xxs" fontWeight="semibold">
        {getDisplayTitle(item)}
      </Text>
      <Text fontSize="xxs">{getLineItemAvailabilityText(item)}</Text>
      {getWoLabel(item) && (
        <Text fontSize="xxs" textColor="warning">
          {getWoLabel(item)}
        </Text>
      )}
      {ordinalStrip}
      {sortedBins.slice(0, MAX_TOOLTIP_BINS).map((bin) => {
        const isNonPickBin =
          bin.storageLocationType === "receiving" ||
          bin.storageLocationType === "storage";
        return (
          <Flex align="center" gap="xxs" key={bin.storageLocationId}>
            <Text
              fontSize="xxs"
              textColor={isNonPickBin ? "tertiary" : undefined}
            >
              {bin.storageLocationName}:{" "}
              {bin.hasPendingReplenishment
                ? "replenishing"
                : bin.lastCountedAt === null && bin.quantity <= 0
                  ? "uncounted"
                  : bin.quantity}
              {getBinExpirationOnlySuffix(bin)}
            </Text>
            {bin.lotNumber && (
              <LotBadge
                expirationDate={bin.expirationDate ?? null}
                lotNumber={bin.lotNumber}
                size="xs"
                type="alternate"
              />
            )}
            {bin.inventoryItemClass === "custom" && (
              <RedoBadge
                color="blue"
                size="xs"
                text="Custom"
                type="alternate"
              />
            )}
          </Flex>
        );
      })}
      {sortedBins.length > MAX_TOOLTIP_BINS && (
        <Text fontSize="xxs" textColor="secondary">
          … and {sortedBins.length - MAX_TOOLTIP_BINS} more
        </Text>
      )}
    </Flex>
  );
}

const catalogEntry = fgColumnEntries.pickability;

const pickabilityBadgeText: Record<PickabilityStatus, string> = {
  all_pickable: "All pickable",
  partially_pickable: "Partially pickable",
  not_pickable: "Not pickable",
  all_pending_wo: "Pending work orders",
  partially_pending_wo: "Partially pending WO",
  picking: "Picking",
};

export const PickabilityColumn: Column2<FulfillmentGroupData> = {
  alignment: ColumnAlignment.LEFT,
  key: catalogEntry.key,
  title: catalogEntry.title,
  renderNormalCell: ({ row }) => {
    // Pickability is only meaningful for open-like groups.
    if (!isOpenIsh(row.status)) {
      return null;
    }
    const status = row.pickabilityStatus as PickabilityStatus | undefined;
    if (!status || !(status in pickabilityBadgeText)) {
      return null;
    }

    const lineItems = row.lineItemPickability;
    const nonPickableItems =
      lineItems?.filter(
        (item) =>
          !isInventoryPickable(item) &&
          !item.allowedByPolicy &&
          !isAlreadyPicked(item) &&
          !item.isNonPicked &&
          !item.hasNoProduct,
      ) ?? [];
    const pickableItems =
      lineItems?.filter(
        (item) =>
          isInventoryPickable(item) ||
          item.allowedByPolicy ||
          isAlreadyPicked(item) ||
          item.isNonPicked ||
          item.hasNoProduct,
      ) ?? [];
    const tooltipContent =
      lineItems && lineItems.length > 0 ? (
        <Flex
          flexDirection="column"
          gap="md"
          maxHeight="half-screen"
          overflowY="auto"
        >
          {nonPickableItems.length > 0 && (
            <Flex flexDirection="column" gap="xs">
              <Flex align="center" gap="xs">
                <StatusDot color="yellow" />
                <Text fontSize="xxs" fontWeight="semibold">
                  Non-pickable items:
                </Text>
              </Flex>
              {nonPickableItems.map(renderLineItemDetails)}
            </Flex>
          )}
          {pickableItems.length > 0 && (
            <Flex flexDirection="column" gap="xs">
              <Flex align="center" gap="xs">
                <StatusDot color="green" />
                <Text fontSize="xxs" fontWeight="semibold">
                  Pickable items:
                </Text>
              </Flex>
              {pickableItems.map(renderLineItemDetails)}
            </Flex>
          )}
        </Flex>
      ) : undefined;

    return (
      <Flex align="flex-start" pl="lg">
        <Tooltip leftAlign title={tooltipContent}>
          <div
            style={
              tooltipContent
                ? {
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                    textUnderlineOffset: "3px",
                  }
                : undefined
            }
          >
            <Text
              fontSize="xs"
              textColor={
                status === "all_pickable"
                  ? "success"
                  : status === "not_pickable"
                    ? "error"
                    : status === "picking"
                      ? "success"
                      : "warning"
              }
            >
              {pickabilityBadgeText[status]}
            </Text>
          </div>
        </Tooltip>
      </Flex>
    );
  },
  renderExpandedCell: ({ row, expansionIndex }) => {
    // Pickability is only meaningful for open-like groups.
    if (!isOpenIsh(row.status)) {
      return null;
    }
    const lineItems = row.lineItemPickability;
    if (!lineItems || lineItems.length === 0) {
      return null;
    }
    // Match by externalId from the expanded items array, not by index
    const expandedItem = getExpandedItems(row)[expansionIndex];
    if (!expandedItem) {
      return null;
    }
    const item = lineItems.find(
      (li) => li.externalId === expandedItem.externalId,
    );
    if (!item) {
      return null;
    }

    const isNonPicked = item.isNonPicked;
    const bins = [...(item.bins ?? [])].sort((a, b) => {
      const aNonBin =
        a.storageLocationType === "receiving" ||
        a.storageLocationType === "storage"
          ? 1
          : 0;
      const bNonBin =
        b.storageLocationType === "receiving" ||
        b.storageLocationType === "storage"
          ? 1
          : 0;
      if (aNonBin !== bNonBin) {
        return aNonBin - bNonBin;
      }
      const aSecondary = a.priority === "secondary" ? 1 : 0;
      const bSecondary = b.priority === "secondary" ? 1 : 0;
      if (aSecondary !== bSecondary) {
        return aSecondary - bSecondary;
      }
      const aExp = a.expirationDate?.getTime() ?? Number.POSITIVE_INFINITY;
      const bExp = b.expirationDate?.getTime() ?? Number.POSITIVE_INFINITY;
      if (aExp !== bExp) {
        return aExp - bExp;
      }
      return b.quantity - a.quantity;
    });

    if (isNonPicked) {
      return (
        <Flex align="flex-start" pl="lg">
          <RedoBadge
            color="success"
            segmentLeading={{ type: "icon", Icon: DotIcon }}
            size="xs"
            text="Non-picking"
            type="primary"
          />
        </Flex>
      );
    }

    if (item.workOrders && item.workOrders.length > 0) {
      const expandedList = getExpandedItems(row);
      const withinItemIndex = expandedList
        .slice(0, expansionIndex)
        .filter((e) => e.externalId === expandedItem.externalId).length;
      const sortedWOs = sortWorkOrdersByProgress(item.workOrders);
      return renderOrdinalExpandedRow(
        sortedWOs[withinItemIndex],
        bins,
        isAlreadyPicked(item),
      );
    }

    const legacyWoLabel = getWoLabel(item);
    return (
      <Flex align="flex-start" flexDirection="column" gap="xs" pl="lg">
        <Flex align="center" gap="sm">
          <RedoBadge
            color={
              isAlreadyPicked(item)
                ? "gray"
                : isInventoryPickable(item) || item.allowedByPolicy
                  ? "success"
                  : "error"
            }
            segmentLeading={{ type: "icon", Icon: DotIcon }}
            size="xs"
            text={getExpandedPickabilityBadgeText(item)}
            type="primary"
          />
        </Flex>
        {legacyWoLabel && (
          <RedoBadge
            color="warning"
            segmentLeading={{ type: "icon", Icon: DotIcon }}
            size="xs"
            text={legacyWoLabel}
            type="primary"
          />
        )}
        {renderBinsBlock(bins)}
      </Flex>
    );
  },
  width: catalogEntry.defaultWidth,
};
